'use client';

import { Slot } from '@radix-ui/react-slot';
import {
   type ComponentProps,
   type ReactNode,
   type RefObject,
   use,
   createContext,
   useCallback,
   useId,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { invariant } from '@ifixit/helpers';
import { cn } from '@ifixit/ui/lib/utils';
import { useControllableState } from '../hooks/use-controllable-state';
import { useStableCallback } from '../hooks/use-stable-callback';
import { afterExit } from '../lib/after-exit';
import { isKeyboardFocus } from '../lib/is-keyboard-focus';
import {
   type PopupPlacement,
   VIEWPORT_MARGIN,
   alignedLeft,
   followAnchor,
   pickSide,
   visibleBounds,
} from '../lib/place-popup';
import { Button } from './button';

/**
 * A tooltip (WAI-ARIA APG): it shows while the trigger is hovered or holds
 * keyboard focus, hides on Escape, and stays while the pointer crosses onto it.
 * The content renders in place and the Popover API lifts it into the top
 * layer, so it stays inside whatever dialog, drawer or sheet owns the trigger.
 */

interface TooltipInternals {
   trigger: HTMLElement | null;
   content: HTMLElement | null;
   // Set once a hover has opened the tooltip, so moving on over the trigger
   // does not reopen it after a press or Escape closed it.
   isHoverOpened: boolean;
}

interface TooltipContextValue {
   open: boolean;
   // A controlled tooltip without onOpenChange cannot close, so Escape and
   // presses go on to whatever is around it.
   isDismissible: boolean;
   contentId: string;
   setOpen: (open: boolean) => void;
   internalsRef: RefObject<TooltipInternals>;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

// The tooltip on screen. Opening another replaces it outright, without either
// one animating, the way native tooltips hand over along a toolbar.
const shownTooltip: { current: { content: HTMLElement; hide: () => void } | null } = {
   current: null,
};

interface TooltipProps {
   open?: boolean;
   onOpenChange?: (open: boolean) => void;
   children?: ReactNode;
}

function Tooltip({ open: openProp, onOpenChange, children }: TooltipProps) {
   const [open, setOpenState] = useControllableState({ prop: openProp, defaultProp: false });
   const contentId = useId();
   const internalsRef = useRef<TooltipInternals>({
      trigger: null,
      content: null,
      isHoverOpened: false,
   });

   // Where the tooltip is headed: what it shows, or what this component last
   // asked for. A trigger whose own click toggles it through its owner's
   // state (TooltipButton) goes around this, so the close that follows the
   // click finds nothing to change.
   const requestedOpenRef = useRef(open);
   useLayoutEffect(() => {
      requestedOpenRef.current = open;
   });

   const setOpen = useStableCallback((next: boolean) => {
      if (next === requestedOpenRef.current) return;
      requestedOpenRef.current = next;
      setOpenState(next);
      onOpenChange?.(next);
   });

   const isDismissible = openProp === undefined || onOpenChange !== undefined;
   // Carpenter builds without the React Compiler.
   const context = useMemo(
      () => ({ open, isDismissible, contentId, setOpen, internalsRef }),
      [open, isDismissible, contentId, setOpen]
   );

   return <TooltipContext value={context}>{children}</TooltipContext>;
}

function TooltipTrigger({
   asChild = false,
   ref,
   onPointerMove,
   onPointerLeave,
   onPointerDown,
   onClick,
   onFocus,
   onBlur,
   ...props
}: ComponentProps<'button'> & { asChild?: boolean }) {
   const { open, contentId, setOpen, internalsRef } = useTooltip();
   // An outer asChild trigger (a PopoverTrigger around this one) hands its
   // ref down as a prop; both need the element.
   const setTriggerNode = useCallback(
      (node: HTMLButtonElement | null) => {
         internalsRef.current.trigger = node;
         if (typeof ref === 'function') ref(node);
         else if (ref != null) ref.current = node;
      },
      [internalsRef, ref]
   );
   const Comp = asChild ? Slot : 'button';
   // Slot hands every prop to the child, where an undefined type would
   // override one the child component sets itself.
   const typeProps = asChild ? {} : { type: 'button' as const };

   return (
      <Comp
         ref={setTriggerNode}
         {...typeProps}
         aria-describedby={open ? contentId : undefined}
         data-slot="tooltip-trigger"
         {...props}
         onPointerMove={event => {
            onPointerMove?.(event);
            const internals = internalsRef.current;
            if (event.pointerType === 'touch' || internals.isHoverOpened) return;
            internals.isHoverOpened = true;
            setOpen(true);
         }}
         onPointerLeave={event => {
            onPointerLeave?.(event);
            const internals = internalsRef.current;
            internals.isHoverOpened = false;
            if (event.pointerType === 'touch') return;
            if (isInside(internals.content, event.relatedTarget)) return;
            if (holdsKeyboardFocus(internals)) return;
            setOpen(false);
         }}
         onPointerDown={event => {
            onPointerDown?.(event);
            // A tap reaches the trigger's own click, which may toggle the
            // tooltip; closing it here first would have that click reopen it.
            if (event.pointerType !== 'touch') setOpen(false);
         }}
         onClick={event => {
            onClick?.(event);
            setOpen(false);
         }}
         onFocus={event => {
            onFocus?.(event);
            if (isKeyboardFocus(event.target)) setOpen(true);
         }}
         onBlur={event => {
            onBlur?.(event);
            if (isInside(internalsRef.current.content, event.relatedTarget)) return;
            setOpen(false);
         }}
      />
   );
}

/**
 * Renders a span, so it can sit inside a paragraph. Its children have to be
 * inline too: `<span className="block">`, not `<p>` or `<div>`.
 */
function TooltipContent({
   className,
   side = 'top',
   align = 'center',
   sideOffset = 0,
   children,
   onPointerLeave,
   onBlur,
   ...props
}: Omit<ComponentProps<'span'>, 'ref'> & {
   side?: 'top' | 'bottom';
   align?: 'start' | 'center' | 'end';
   sideOffset?: number;
}) {
   const { open, isDismissible, contentId, setOpen, internalsRef } = useTooltip();
   const [mounted, setMounted] = useState(open);
   if (open && !mounted) setMounted(true);

   const setContentNode = useCallback(
      (node: HTMLSpanElement | null) => {
         internalsRef.current.content = node;
      },
      [internalsRef]
   );

   // A layout effect, not useStableCallback: that one updates after paint, so
   // the first placement would still see the previous render's props.
   const placementRef = useRef({ side, align, sideOffset });
   useLayoutEffect(() => {
      placementRef.current = { side, align, sideOffset };
   });

   useLayoutEffect(() => {
      const { content, trigger } = internalsRef.current;
      if (!open || !content || !trigger) return;

      function reposition() {
         if (!content || !trigger) return;
         const visible = placeTooltip(content, trigger, placementRef.current);
         if (!visible) setOpen(false);
      }
      function handlePointerDown(event: PointerEvent) {
         if (isInside(content, event.target) || isInside(trigger, event.target)) return;
         setOpen(false);
      }
      // Dialogs, sheets and the task composer listen for Escape on the
      // document; the window sees it first, so the key closes only the tooltip.
      function handleKeyDown(event: KeyboardEvent) {
         if (event.key !== 'Escape' || !isDismissible) return;
         event.preventDefault();
         event.stopPropagation();
         // A link in the content is about to hide with the focus on it. The
         // focus goes back while the tooltip is still open, so it arriving on
         // the trigger does not reopen it.
         if (isInside(content, document.activeElement)) trigger?.focus({ preventScroll: true });
         setOpen(false);
      }

      const previous = shownTooltip.current;
      const isHandover =
         previous !== null && previous.content !== content && previous.content.isConnected;
      if (isHandover) {
         previous.content.dataset.instant = '';
         previous.hide();
      }
      shownTooltip.current = { content, hide: () => setOpen(false) };
      content.toggleAttribute('data-instant', isHandover);
      content.showPopover?.();
      // Placing measures the content, which settles its first style without
      // a transition; only then may later changes animate again.
      reposition();
      delete content.dataset.instant;

      const stopFollowing = followAnchor(content, reposition);
      window.addEventListener('keydown', handleKeyDown, { capture: true });
      document.addEventListener('pointerdown', handlePointerDown, { capture: true });
      return () => {
         stopFollowing();
         window.removeEventListener('keydown', handleKeyDown, { capture: true });
         document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
         content.hidePopover?.();
      };
   }, [open, isDismissible, setOpen, internalsRef]);

   useLayoutEffect(() => {
      const { content } = internalsRef.current;
      if (open || !mounted) return;
      return afterExit(content, () => {
         if (shownTooltip.current?.content === content) shownTooltip.current = null;
         setMounted(false);
      });
   }, [open, mounted, internalsRef]);

   if (!mounted) return null;
   return (
      // A span, since in place it can land inside a paragraph.
      <span
         ref={setContentNode}
         id={contentId}
         role="tooltip"
         // "manual": the component owns dismissal, the browser only supplies
         // the top layer.
         popover="manual"
         data-slot="tooltip-content"
         data-state={open ? 'open' : 'closed'}
         className={cn(
            'bg-gray-950 text-white z-tooltip fixed inset-auto m-0 w-fit max-w-3xs md:max-w-2xs overflow-visible rounded-lg border-0 px-3 py-1.5 text-xs',
            // Rendered in place, it would otherwise take on the type of
            // whatever holds its trigger: a table header, a badge, a heading.
            'text-start font-normal not-italic normal-case tracking-normal whitespace-normal',
            'opacity-0 transition-[opacity,scale,display,overlay] transition-discrete duration-150 ease-out motion-safe:scale-95',
            'data-[state=open]:opacity-100 motion-safe:data-[state=open]:scale-100 starting:data-[state=open]:opacity-0 motion-safe:starting:data-[state=open]:scale-95',
            'data-[state=closed]:pointer-events-none data-[instant]:transition-none',
            // Bridges the gap to the trigger, so the pointer crossing it
            // never leaves both.
            'before:absolute before:inset-x-0 before:h-(--tooltip-gap) data-[side=bottom]:before:bottom-full data-[side=top]:before:top-full',
            // The arrow, drawn under the text.
            'after:absolute after:left-(--tooltip-arrow-x) after:-z-10 after:size-2.5 after:-translate-x-1/2 after:rotate-45 after:rounded-[2px] after:bg-inherit',
            'data-[side=bottom]:after:bottom-full data-[side=bottom]:after:translate-y-[calc(50%+2px)] data-[side=top]:after:top-full data-[side=top]:after:-translate-y-[calc(50%+2px)]',
            className
         )}
         onPointerLeave={event => {
            onPointerLeave?.(event);
            if (event.pointerType === 'touch') return;
            const internals = internalsRef.current;
            if (isInside(internals.trigger, event.relatedTarget)) return;
            if (holdsKeyboardFocus(internals)) return;
            setOpen(false);
         }}
         onBlur={event => {
            onBlur?.(event);
            const { trigger, content } = internalsRef.current;
            if (isInside(content, event.relatedTarget)) return;
            if (isInside(trigger, event.relatedTarget)) return;
            setOpen(false);
         }}
         {...props}
      >
         {children}
      </span>
   );
}

interface TooltipButtonProps extends ComponentProps<'button'> {
   tooltip: ReactNode;
   tooltipProps?: Omit<ComponentProps<typeof TooltipContent>, 'children'>;
}

/** A button that also toggles its tooltip on press, for touch screens. */
function TooltipButton({
   tooltip,
   tooltipProps,
   className,
   onClick,
   children,
   ...buttonProps
}: TooltipButtonProps) {
   const [open, setOpen] = useState(false);

   return (
      <Tooltip open={open} onOpenChange={setOpen}>
         <TooltipTrigger asChild>
            <Button
               className={cn(className, open && 'bg-accent')}
               onClick={e => {
                  setOpen(!open);
                  onClick?.(e);
               }}
               {...buttonProps}
            >
               {children}
            </Button>
         </TooltipTrigger>
         <TooltipContent {...tooltipProps}>{tooltip}</TooltipContent>
      </Tooltip>
   );
}

// How far the content sits from its trigger before sideOffset: the arrow's
// reach plus the room it leaves.
const ARROW_GAP = 10;
// Keeps the arrow clear of the rounded corners.
const ARROW_INSET = 12;

function useTooltip() {
   const context = use(TooltipContext);
   invariant(context, 'Tooltip parts must be rendered inside a Tooltip');
   return context;
}

function isInside(container: Element | null, target: EventTarget | null) {
   return target instanceof Node && container?.contains(target) === true;
}

// Focus the keyboard put on the trigger or into the content keeps the
// tooltip open after the pointer leaves (WCAG 1.4.13); a mouse user's
// clicked, focused button does not.
function holdsKeyboardFocus({ trigger, content }: TooltipInternals) {
   const focused = document.activeElement;
   if (!focused || !(isInside(trigger, focused) || isInside(content, focused))) return false;
   return isKeyboardFocus(focused);
}

// Returns false once the trigger has scrolled out of the visible bounds.
function placeTooltip(
   content: HTMLElement,
   trigger: HTMLElement,
   { side, align, sideOffset }: PopupPlacement
) {
   const rect = trigger.getBoundingClientRect();
   const bounds = visibleBounds();
   if (
      rect.bottom < bounds.top ||
      rect.top > bounds.bottom ||
      rect.right < bounds.left ||
      rect.left > bounds.right
   ) {
      return false;
   }

   // Measured against the left edge, where it has the whole width to wrap
   // in; it lands where it keeps that width. A pinch zoom can leave less
   // than its own max-width.
   const maxWidth = bounds.right - bounds.left - 2 * VIEWPORT_MARGIN;
   content.style.left = `${bounds.left}px`;
   content.style.maxWidth = '';
   if (content.offsetWidth > maxWidth) content.style.maxWidth = `${maxWidth}px`;
   const width = content.offsetWidth;
   const height = content.offsetHeight;

   const gap = sideOffset + ARROW_GAP;
   const { placedBelow } = pickSide(rect, bounds, side, gap, height);
   const placedTop = placedBelow ? rect.bottom + gap : rect.top - gap - height;
   // With room on neither side (a deep pinch zoom) it covers the trigger
   // rather than leave the screen.
   const y = Math.max(
      bounds.top + VIEWPORT_MARGIN,
      Math.min(placedTop, bounds.bottom - VIEWPORT_MARGIN - height)
   );
   content.style.top = `${y}px`;
   content.dataset.side = placedBelow ? 'bottom' : 'top';

   const x = alignedLeft(trigger, rect, bounds, align, width);
   content.style.left = `${x}px`;

   const arrowX = Math.min(
      Math.max(rect.left + rect.width / 2 - x, ARROW_INSET),
      width - ARROW_INSET
   );
   content.style.setProperty('--tooltip-arrow-x', `${arrowX}px`);
   content.style.setProperty('--tooltip-gap', `${gap}px`);
   content.style.transformOrigin = `${arrowX}px ${placedBelow ? '0' : '100%'}`;
   return true;
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipButton };

export type { TooltipButtonProps };
